import { FC, useEffect, useMemo, useState } from 'react';
import {
    CalendarBackgroundModal,
    CalendarModalWrapper,
} from '../Calendar/styles';

import {
    CalendarWithWavesContainer,
    CustomDropdown,
    TimeButton,
} from './styles';

import { LuSparkles } from 'react-icons/lu';
import animations from '../../helpers/animations';
import { DateUtils } from '../../helpers/dateUtils';
import { WaveLevelEnum } from '../../helpers/utils';
import { useGetShopItemsWaves } from '../../hooks/useGetShopItemsWaves';
import {
    CartServiceType,
    CartValuesServiceStore,
    useShoppingCartStore,
} from '../../store/shoppingCart/shoppingCartStore';
import { SideWave } from '../../templates/ModalShopping/pieces/SideWave';
import { BigButton } from '../BigButton';
import { CalendarProps } from '../Calendar';
import { CustomReactCalendar } from '../Calendar/CustomReactCalendar';
import { Dropdown } from '../Dropdown';

interface CalendarWithWavesProps extends Omit<CalendarProps, 'onClose'> {
    onClose?: (d: CartValuesServiceStore | null) => void;
    defaultData: CartValuesServiceStore;
}

export const CalendarWithWaves: FC<CalendarWithWavesProps> = ({
    onClose,
    defaultData,
}) => {
    const { cartValues } = useShoppingCartStore();
    const [date, setDate] = useState<Date>(defaultData.date);
    const [selectedTime, setSelectedTime] = useState<string | undefined>(
        defaultData?.scheduleHour
    );
    const [waveSide, setWaveSide] = useState<'Left' | 'Right'>(
        defaultData.waveSide!
    );
    const [currentCategorySelected, setCurrentCategory] = useState<string>(
        defaultData.title
    );

    const { data } = useGetShopItemsWaves({
        Date: date,
        ProductWaveSide: waveSide,
    });

    const elementsWavesExhibitionName = useMemo(() => {
        if (!data?.length) return [];

        return data.map(wave => ({
            id: String(wave.id),
            value: wave.exhibitionName,
        }));
    }, [data]);

    const findSelected = useMemo(
        () =>
            data?.find(wave => wave.exhibitionName === currentCategorySelected),
        [currentCategorySelected, data]
    );

    //Pegar o array de horários com base na data e se está disponível
    const timesSchedules = useMemo(() => {
        if (!findSelected?.schedules?.length || !cartValues) return null;

        const findInCart = cartValues.filter(
            item =>
                item.title === currentCategorySelected &&
                DateUtils.areDatesEqual(date, item.date) &&
                item.idCart !== defaultData.idCart
        );

        // Comparando se está disponível, e sem ser o horario atual selecionado
        return findSelected.schedules
            .filter(item => {
                const countInCart = findInCart.filter(
                    cartItem => cartItem.scheduleHour === item.name
                ).length;

                return item.available > countInCart;
            })
            .map(item => ({ id: item.id, value: item.name }));
    }, [findSelected?.schedules, cartValues, currentCategorySelected, date]);

    const findSelectedTime = useMemo(
        () => findSelected?.schedules.find(item => item.name === selectedTime),
        [findSelected?.schedules, selectedTime]
    );

    const generalData: CartValuesServiceStore = {
        id: String(findSelected?.id || defaultData.id),
        date,
        scheduleHour: findSelectedTime?.name,
        scheduleId: findSelectedTime?.id,
        type: CartServiceType.Waves,
        waveLevel:
            findSelected?.waveLevel === WaveLevelEnum.REEF
                ? WaveLevelEnum.REEF
                : WaveLevelEnum.BAY,
        value: typeof defaultData.giftId === "number" ? 0 : (findSelected?.value || defaultData.value),
        title: findSelected?.exhibitionName || defaultData.title,
        available: findSelectedTime?.available || defaultData.available,
        waveSide,
        giftId: defaultData.giftId,
        isFirstHeat: defaultData.isFirstHeat
    };

    const isDataValid = (generalData.scheduleHour && generalData.scheduleId)

    const handleSafeClose = (isCancel: boolean) => {
        if (!onClose) return;
        if (isCancel) return onClose(defaultData);
        if (!generalData.scheduleHour || !generalData.scheduleId) return onClose(defaultData);

        onClose(generalData);
    };

    useEffect(() => {
        if (!data?.length) return;
        const existsInData = data.some(w => w.exhibitionName === currentCategorySelected);
        if (existsInData) return;
        const defaultExists = data.some(w => w.exhibitionName === defaultData.title);
        setCurrentCategory(defaultExists ? defaultData.title : (data[0]?.exhibitionName ?? ''));
    }, [data]);

    return (
        <CalendarBackgroundModal
            onClick={e => {
                e.stopPropagation();
                onClose && onClose(null);
            }}
            {...animations.calendar}
        >
            <CalendarModalWrapper
                onClick={e => {
                    e.stopPropagation();
                }}
            >
                <CalendarWithWavesContainer>
                    <h2 className="title">Calendário</h2>

                    <div className="category-wrapper">
                        <CustomDropdown>
                            <Dropdown
                                elements={elementsWavesExhibitionName}
                                title="Selecione uma categoria"
                                setCurrentSelected={setCurrentCategory}
                                currentSelected={currentCategorySelected}
                            />
                        </CustomDropdown>
                        <SideWave
                            direction={waveSide}
                            onSelect={setWaveSide}
                        />
                    </div>
                    {/* <PseudoCalendar
                        onNext={modifyValueInDays(1)}
                        onPrevious={modifyValueInDays(-1)}
                        date={date!}
                    /> */}
                    <div className="time-wrapper">
                        {timesSchedules?.length && timesSchedules.length > 0 ? (
                            timesSchedules.map((time, index) => (
                                <TimeButton
                                    aria-selected={time.value === selectedTime}
                                    onClick={() =>
                                        setSelectedTime(String(time.value))
                                    }
                                >
                                    {index === 0 && <LuSparkles />}
                                    {String(time.value)}
                                </TimeButton>
                            ))
                        ) : (
                            <TimeButton>Sem horários disponíveis</TimeButton>
                        )}
                    </div>
                    <CustomReactCalendar
                        setValue={setDate}
                        value={date}
                        pricesProductId={generalData.id}
                        forcedPrice={(typeof defaultData.giftId === "number") ? 0 : undefined}
                    />
                    <div className="line-divider"></div>
                    <div className="buttons-wrapper">
                        <BigButton
                            color="secondary-alt"
                            bgColor="secondary"
                            children={'Cancelar'}
                            onClick={e => {
                                e.stopPropagation();
                                if (onClose) handleSafeClose(true);
                            }}
                        />
                        <BigButton
                            color="primary"
                            bgColor={!isDataValid ? "secondary" : "primary"}
                            children={'Salvar'}
                            onClick={e => {
                                e.stopPropagation();
                                if (onClose) handleSafeClose(false);
                            }}
                            disabled={!isDataValid}
                            cursorBlocked={!isDataValid}
                        />
                    </div>
                </CalendarWithWavesContainer>
            </CalendarModalWrapper>
        </CalendarBackgroundModal>
    );
};
